import { ActionModal } from '@kcftech/design-system';
import {
    AssetCategoryCreateRequest,
    IAssetCategoryResponse,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    AssetCategoryFormContent,
    AssetCategoryFormData,
} from 'pages/Settings/AssetType/AssetCategoryFormContent';
import { useForm } from 'react-hook-form';

export type CategoryCreationInfo = Readonly<{
    parentCategory: IAssetCategoryResponse | undefined;
    siblingShortNames: string[];
}>;

export type CreateAssetCategoryProps = CategoryCreationInfo &
    Readonly<{
        assetTypeId: number;
        onClose: (createdCategory: IAssetCategoryResponse | undefined) => void;
    }>;

export function CreateAssetCategoryModal({
    assetTypeId,
    parentCategory,
    siblingShortNames,
    onClose,
}: CreateAssetCategoryProps) {
    const { showToast } = useNotificationContext();
    const form = useForm<AssetCategoryFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
    } = form;

    const { mutate: createCategory, isPending } = useMutation({
        meta: { name: 'create-asset-classification-category' },
        mutationFn: async (formData: AssetCategoryFormData) =>
            sdClient.createAssetClassificationCategory({
                body: new AssetCategoryCreateRequest({
                    AssetTypeId: assetTypeId,
                    ParentCategoryId: parentCategory?.Id,
                    ShortName: formData.name.trim(),
                }),
            }),

        onSuccess: (result) => {
            onClose(result);
            showToast({
                type: 'success',
                content: 'Successfully created asset category',
            });
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to create asset category' }),
    });

    const onSave = handleSubmit((formData) => createCategory(formData));

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose(undefined)}
            title="Create Asset Category"
            height={350}
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <AssetCategoryFormContent
                form={form}
                siblingShortNames={siblingShortNames}
                parentCategoryName={parentCategory?.Name}
            />
        </ActionModal>
    );
}
